import { getBooking } from '../../../api/bookings';
import { getExperience, getExperiences } from '../../../api/experiences';
import { getHotel, getHotelsAvailability } from '../../../api/hotels';
import { dayjs, formatDate } from '@koob/dates';
import { getPricingForCompositionExtra } from '@koob/utils';

const formatPrice = (price, pax = 1) => {
  return (parseFloat(price) * pax).toFixed(2);
};

// KD-3650 - an additional service is priced on the pax count of its own
// occurrence, not on the whole party. Options saved before the ticket carry no
// composition of their own and keep falling back on the trip one.
const getOptionComposition = (option, trip) => ({
  adults: option?.adultCount ?? trip?.adultPax ?? trip?.maxPax,
  childrenBirthdates:
    option?.childrenBirthdates ?? trip?.childrenBirthdates ?? [],
});

const getHotelPrice = async ({ hotelId, trip, dayIndex, locale }) => {
  return new Promise(resolve => {
    const requestFilters = {
      hotels: [hotelId],
      startAt: formatDate(
        dayjs(trip.startDate).add(dayIndex, 'days'),
        'YYYY-MM-DD',
      ),
      endAt: formatDate(
        dayjs(trip.startDate).add(dayIndex + 1, 'days'),
        'YYYY-MM-DD',
      ),
      rooms: [
        {
          adults: trip.maxPax,
          children: 0,
          childrenBirthdates: [],
          firstAdultNationality: '',
        },
      ],
      cities: [],
      countries: [],
      kinds: [],
      regions: [],
      styles: [],
      sustainability: null,
    };

    const getHotelPrice = async () => {
      const hotelPrice = (
        await getHotelsAvailability({ filters: requestFilters, locale })
      )?.data;
      if (hotelPrice.state === 'inProgress') {
        setTimeout(() => getHotelPrice(), 3000);
      } else {
        resolve(hotelPrice?.data?.[0]);
      }
    };

    getHotelPrice();
  });
};

export const fetchTripReceiptData = async ({ trip, locale }) => {
  const bookings = await Promise.all(
    trip.bookings.map(async booking => {
      if (!booking.experiencesBooked?.length) {
        return (await getBooking({ bookingId: booking.id }))?.data;
      } else {
        return booking;
      }
    }),
  );

  let experiences = [];
  let hotels = [];

  if (bookings?.length === 0) {
    const newExperiences = await trip.tripDays?.reduce(
      async (acc, day, index) => {
        const accumulatedExperiences = await acc;

        if (day.experience) {
          if (day.experienceDayIndex === 0) {
            const experienceData = (
              await getExperience({ experienceId: day.experience.id, locale })
            )?.data;
            const experiencePrice = (
              await getExperiences({
                filters: {
                  experiences: [day.experience.id],
                  experienceComposition: {
                    adults: trip?.adultPax ?? trip?.maxPax,
                    children: trip?.childrenBirthdates.length ?? 0,
                    childrenBirthdates: trip?.childrenBirthdates ?? [],
                    count:
                      trip?.adultPax + trip?.childrenBirthdates.length ??
                      trip?.maxPax,
                    firstAdultNationality: '',
                  },
                  startAt: formatDate(
                    dayjs(trip.startDate).add(index, 'days'),
                    'YYYY-MM-DD',
                  ),
                  endAt: formatDate(
                    dayjs(trip.startDate).add(
                      index + experienceData.durationDays,
                      'days',
                    ),
                    'YYYY-MM-DD',
                  ),
                  isForTripPrice: true,
                },
                locale,
              })
            )?.data?.[0]?.price;

            accumulatedExperiences.push({
              ...experienceData,
              priceObject: experiencePrice,
              price: experiencePrice?.price,
              currency: experiencePrice?.currency,
            });
          }

          const experiencePrice = accumulatedExperiences.findLast(
            experience => experience.id === day.experience.id,
          );

          for (const option of day.options) {
            const optionData = (
              await getExperience({ experienceId: option.optionId, locale })
            )?.data;
            const optionPrice = experiencePrice?.extras?.find(
              extra => extra.id === option.optionId,
            );
            const pricing = getPricingForCompositionExtra({
              composition: getOptionComposition(option, trip),
              pricings: optionPrice?.pricings,
            });

            accumulatedExperiences.push({
              ...optionData,
              // KD-3650 — one entry per occurrence, and they all carry the
              // same experience id: without the occurrence key the receipt
              // read them all back as the first one, and every row showed its
              // price and its pax count.
              occurrenceKey: option.occurrenceKey ?? null,
              composition: getOptionComposition(option, trip),
              price: pricing && formatPrice(pricing.price, 1),
              currency: experiencePrice?.currency,
            });
          }
        }
        return accumulatedExperiences;
      },
      Promise.resolve([]),
    );

    const globalOptions = await Promise.all(
      trip.globalOptions.map(async option => {
        const optionData = (
          await getExperience({ experienceId: option.optionId })
        )?.data;
        const optionPriceExperience = newExperiences.find(experience =>
          experience.extras?.find(
            extra =>
              extra.id === option.optionId ||
              extra.required?.find(
                required => required.id === option.optionId,
              ) ||
              extra.suggested?.find(
                suggested => suggested.id === option.optionId,
              ),
          ),
        );
        const optionPrice = optionPriceExperience?.priceObject?.extras?.find(
          extra => extra.id === option.optionId,
        );
        const pricing = getPricingForCompositionExtra({
          composition: getOptionComposition(option, trip),
          pricings: optionPrice?.pricings,
        });

        return {
          ...optionData,
          occurrenceKey: option.occurrenceKey ?? null,
          composition: getOptionComposition(option, trip),
          price: pricing && formatPrice(pricing.price, 1),
          currency: optionPriceExperience?.priceObject?.currency,
        };
      }),
    );

    experiences = [...experiences, ...newExperiences, ...globalOptions];

    hotels = await Promise.all(
      trip.tripDays.map(async (day, index) => {
        if (!day.hotelId) {
          return null;
        }

        const hotelData = (await getHotel({ hotelId: day.hotelId }))?.data;
        const hotelPrice = await getHotelPrice({
          hotelId: day.hotelId,
          trip,
          dayIndex: index,
        });

        return {
          ...hotelData,
          id: day.hotelId,
          price: formatPrice(hotelPrice?.price),
          currency: hotelPrice?.currency,
        };
      }),
    );
  }

  return {
    bookings,
    experiences,
    hotels,
  };
};
